/**
 * Composable que sincroniza la config IA del usuario de forma cifrada
 * con la nube (tabla `user_settings`).
 *
 * Modelo "nube gana": al desbloquear, si la nube tiene una versión más
 * nueva (`updated_at`), sobreescribe la config local. Los cambios locales
 * se empujan a la nube con debounce.
 *
 * Requiere `auth.isKeyUnlocked` (la `CryptoKey` derivada en memoria).
 * Si la tabla `user_settings` no existe en la instancia Supabase, todas
 * las operaciones fallan silenciosamente (log + return) — la config IA
 * sigue funcionando localmente.
 */
import { useSettingsStore } from '@/stores/settings'
import { useAuthStore } from '@/sync/auth'
import { getSupabase } from '@/sync/supabase'
import { decrypt, encrypt, fromBase64, type SealedPayload } from '@/utils/crypto'
import type { AiSettings } from '@/types/settings'
import { logError } from '@/db/error-log'

type UserSettingsRow = SealedPayload & { updated_at: string }

export function useAiSettingsSync() {
  const settings = useSettingsStore()
  const auth = useAuthStore()

  /**
   * Lee la config IA cifrada de la nube, la descifra y, SOLO si es más
   * nueva que la última versión local sincronizada Y tiene datos mínimos
   * válidos, la aplica al store (nube gana).
   *
   * Devuelve la config descifrada o `null` si no hay fila / la clave no
   * está disponible / la tabla no existe / la nube no es más nueva / el
   * remoto está vacío o corrupto.
   */
  async function pullEncryptedSettings(): Promise<AiSettings | null> {
    if (!auth.userId || !auth.encryptionKey) return null
    const client = getSupabase(settings.settings.sync)
    if (!client) return null

    try {
      const { data, error } = await client
        .from('user_settings')
        .select('ciphertext, iv, salt, updated_at')
        .eq('user_id', auth.userId)
        .maybeSingle<UserSettingsRow>()
      if (error) throw error
      if (!data) {
        // No hay fila en la nube: nada que descargar. La config local es lo
        // que hay; el push subirá los cambios cuando el usuario edite.
        settings.setAiSyncStatus('local')
        return null
      }

      // Decidir si el remoto debe aplicarse sobre lo local.
      // - `lastSyncedAt === null` → este dispositivo nunca ha sincronizado.
      //   Puede ser un dispositivo realmente nuevo (local vacío) o uno con
      //   datos residuales (apiKey de pruebas). En AMBOS casos, si la nube
      //   tiene una versión real (`updated_at`), la aplicamos: la nube es la
      //   fuente de verdad. Antes un dispositivo con `lastSyncedAt=null` +
      //   apiKey residual se quedaba sin recibir la config de la nube (bug).
      // - `lastSyncedAt` con valor → comparar timestamps; si el remoto no
      //   es más nuevo, mantener lo local.
      const localSyncedAt = settings.settings.ai.lastSyncedAt
      const isNewDevice = localSyncedAt === null
      if (!isNewDevice && !isRemoteNewer(data.updated_at, localSyncedAt!)) {
        // Mismo timestamp o local más nueva: mantener lo local, sólo
        // refrescar la marca en memoria por si difería.
        settings.markAiSynced(data.updated_at)
        settings.setAiSyncStatus('synced')
        return null
      }

      const json = await decrypt(auth.encryptionKey, data)
      const parsed = JSON.parse(json) as AiSettings

      // Defensa: si el remoto está vacío o corrupto (p. ej. un push
      // accidental de defaults sin keys), NO lo aplicamos — mantener lo
      // local para no perder la configuración real.
      if (!isRemoteAiUsable(parsed)) {
        const p = parsed as Partial<AiSettings>
        logError({
          source: 'ai-settings-sync',
          message: 'pull descartado: remoto sin datos mínimos válidos',
          context: `active=${p.activeProvider}, routing=${p.routing}`,
        })
        settings.markAiSynced(data.updated_at)
        settings.setAiSyncStatus('error')
        return null
      }

      // Nube gana (es más nueva y válida): aplicar al store sin disparar
      // el push, y registrar el timestamp tanto en memoria como persistido.
      settings.markAiSynced(data.updated_at)
      await settings.applyRemoteAi({ ...parsed, lastSyncedAt: data.updated_at })
      settings.setAiSyncStatus('synced')
      return parsed
    } catch (e) {
      // Distinguir un fallo de descifrado (clave/salt incorrecto) de otros
      // errores: AES-GCM lanza un error cuyo nombre contiene 'Operation'.
      const isDecryptError =
        e instanceof Error && /operation|decrypt|invalid/i.test(e.name + ' ' + e.message)
      logError({
        source: 'ai-settings-sync',
        message: isDecryptError
          ? 'pull failed: fallo de descifrado (clave o salt incorrecto)'
          : 'pull failed',
        context: e instanceof Error ? `${e.name}: ${e.message}` : String(e),
      })
      settings.setAiSyncStatus('error')
      return null
    }
  }

  /**
   * ¿Es el `remoteAt` posterior al `localAt`? Comparación lexicográfica
   * sobre timestamps ISO-8601 UTC (válido porque ambos vienen en el mismo
   * formato `toISOString()` / `timestamptz` de Postgres).
   */
  function isRemoteNewer(remoteAt: string, localAt: string): boolean {
    return remoteAt > localAt
  }

  /**
   * ¿Tiene el remoto los campos mínimos para considerarse una config IA
   * válida (no un default vacío)? Exigimos `activeProvider` conocido y
   * `providers` con estructura. La "validez" se cumple si hay ALGUNA de:
   *   - el provider activo tiene `apiKey` (modo direct), o
   *   - hay `proxyEndpoint`/`proxyToken` (modo proxy — la key vive en el
   *     worker, así que el provider activo puede no tener apiKey), o
   *   - el provider activo tiene `model` o `baseUrl` (configuración parcial
   *     legítima, p. ej. modelo personalizado).
   * Antes se exigía `model||baseUrl||apiKey` del provider activo, lo que
   * descartaba configs de proxy válidas donde el provider activo venía
   * vacío (bug: config IA vacía en dispositivo nuevo con modo proxy).
   */
  function isRemoteAiUsable(ai: unknown): ai is AiSettings {
    if (!ai || typeof ai !== 'object') return false
    const a = ai as Partial<AiSettings>
    if (!a.activeProvider) return false
    if (!a.providers || typeof a.providers !== 'object') return false
    const active = a.providers[a.activeProvider]
    if (!active) return false
    // Modo proxy legítimo: la autenticación vive en el worker.
    if (a.proxyEndpoint || a.proxyToken) return true
    // Modo direct o configuración parcial del provider activo.
    return Boolean(active.model || active.baseUrl || active.apiKey)
  }

  /**
   * Cifra y sube la config IA actual a la nube (upsert por user_id).
   * Silencioso en caso de error (log local).
   */
  async function pushEncryptedSettings(): Promise<void> {
    if (!auth.userId || !auth.encryptionKey || !auth.saltB64) return
    const client = getSupabase(settings.settings.sync)
    if (!client) return

    try {
      const saltBytes = fromBase64(auth.saltB64)
      const payload = await encrypt(
        auth.encryptionKey,
        JSON.stringify(settings.settings.ai),
        saltBytes,
      )
      const { error } = await client.from('user_settings').upsert(
        {
          user_id: auth.userId,
          ciphertext: payload.ciphertext,
          iv: payload.iv,
          salt: payload.salt,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id' },
      )
      if (error) throw error
      const nowIso = new Date().toISOString()
      // Actualizar tanto la marca en memoria como la persistida (dentro de
      // `ai.lastSyncedAt`), para que al reabrir la app el pull sepa que lo
      // local ya está al día con esta versión de la nube.
      settings.markAiSynced(nowIso)
      await settings.updateAiLastSyncedAt(nowIso)
      settings.setAiSyncStatus('synced')
    } catch (e) {
      logError({ source: 'ai-settings-sync', message: 'push failed', context: e instanceof Error ? e.message : String(e) })
      settings.setAiSyncStatus('error')
    }
  }

  return { pullEncryptedSettings, pushEncryptedSettings }
}
